/**
 * One account, in full.
 *
 * The tabs are the four questions support actually gets asked: what did they
 * open, what do they hold, where did their money go, and can this rip be
 * verified. Seed pairs are here because "was my rip fair" is answerable from
 * the commitment and the nonce, and this is where someone will look for them.
 */
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { api, type AdminUserDetail } from '../lib/api'
import {
  Empty,
  ErrorNote,
  Loading,
  Panel,
  Stat,
  StatusChip,
  cents,
  dateTime,
  multiple,
  percent,
  shortHash,
  timeAgo,
} from '../components/ui'

const TABS = ['Rips', 'Holdings', 'Ledger', 'Settlement', 'Fairness'] as const
type Tab = (typeof TABS)[number]

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [tab, setTab] = useState<Tab>('Rips')

  const { data, isLoading, error } = useQuery({
    queryKey: ['user', id],
    queryFn: () => api.get<AdminUserDetail>(`/users/${id}`),
    enabled: Boolean(id),
  })

  if (isLoading) return <Loading />
  if (error || !data) return <ErrorNote>No such account.</ErrorNote>

  const { user } = data
  const net = user.returnedCents - user.stakedCents

  return (
    <div className="space-y-5">
      <Link to="/users" className="text-xs hover:underline" style={{ color: 'var(--color-faint)' }}>
        ← All accounts
      </Link>

      <Panel>
        <div className="flex flex-wrap items-start justify-between gap-4 border-b p-5">
          <div className="min-w-0">
            <h1 className="text-xl font-bold tracking-tight">{user.username ?? 'No handle'}</h1>
            <div className="hash mt-1 break-all">{user.address}</div>
            {user.bio && (
              <p className="mt-2 max-w-lg text-xs" style={{ color: 'var(--color-muted)' }}>
                {user.bio}
              </p>
            )}
            <div className="mt-2 text-xs" style={{ color: 'var(--color-faint)' }}>
              Joined {dateTime(user.createdAt)} · last seen {timeAgo(user.lastSeenAt)} ago ·{' '}
              {user.profilePublic ? 'public profile' : 'private profile'}
            </div>
          </div>
        </div>

        <div className="grid gap-6 p-5 sm:grid-cols-3 lg:grid-cols-6">
          <Stat label="Balance" value={cents(user.balanceCents)} />
          <Stat
            label="Vault"
            value={cents(data.vaultValueCents)}
            sub={`${data.positions.length} positions`}
          />
          <Stat label="Staked" value={cents(user.stakedCents)} />
          <Stat
            label="Net"
            value={cents(net, { sign: true })}
            tone={net >= 0 ? 'up' : 'down'}
            sub="returned − staked"
          />
          <Stat
            label="Locked bonus"
            value={cents(user.lockedCents)}
            sub={user.referredById ? 'was referred' : 'not referred'}
            // Bonus outstanding on an account that has not played is the shape
            // a farmed referral takes; worth seeing next to the balance.
            tone={user.lockedCents > 0 ? 'warn' : undefined}
          />
          <Stat
            label="Realised RTP"
            value={percent(user.realisedRtp)}
            // A player far above the published band is either very lucky or
            // has found something; either way it is worth a second look.
            tone={user.realisedRtp !== null && user.realisedRtp > 1 ? 'warn' : undefined}
          />
        </div>
      </Panel>

      <div className="flex gap-1">
        {TABS.map((t) => (
          <button
            key={t}
            className={`btn btn-sm ${tab === t ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setTab(t)}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Rips' && <Rips data={data} />}
      {tab === 'Holdings' && <Holdings data={data} />}
      {tab === 'Ledger' && <Ledger data={data} />}
      {tab === 'Settlement' && <Settlement data={data} />}
      {tab === 'Fairness' && <Fairness data={data} />}
    </div>
  )
}

function Rips({ data }: { data: AdminUserDetail }) {
  if (data.opens.length === 0) return <Panel><Empty>No rips yet.</Empty></Panel>
  return (
    <Panel title="Recent rips">
      <div className="overflow-x-auto">
        <table className="grid-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Pack</th>
              <th className="text-right">Paid</th>
              <th className="text-right">Returned</th>
              <th className="text-right">Multiple</th>
              <th>Best</th>
              <th className="text-right">Nonce</th>
            </tr>
          </thead>
          <tbody>
            {data.opens.map((o) => (
              <tr key={o.id}>
                <td className="whitespace-nowrap" style={{ color: 'var(--color-muted)' }}>
                  {dateTime(o.openedAt)}
                </td>
                <td className="font-medium">{o.packName}</td>
                <td className="tnum text-right">{cents(o.priceCents)}</td>
                <td className="tnum text-right">{cents(o.totalCents)}</td>
                <td
                  className="tnum text-right font-semibold"
                  style={{ color: o.multiple >= 1 ? 'var(--color-up)' : 'var(--color-down)' }}
                >
                  {multiple(o.multiple)}
                </td>
                <td><StatusChip status={o.best} /></td>
                <td className="tnum text-right" style={{ color: 'var(--color-faint)' }}>
                  {o.nonce}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  )
}

function Holdings({ data }: { data: AdminUserDetail }) {
  if (data.positions.length === 0) return <Panel><Empty>Nothing held.</Empty></Panel>
  return (
    <Panel title={`Holdings · ${cents(data.vaultValueCents)}`}>
      <div className="overflow-x-auto">
        <table className="grid-table">
          <thead>
            <tr>
              <th>Token</th>
              <th className="text-right">Quantity</th>
              <th>Rarity</th>
              <th className="text-right">Cost basis</th>
              <th className="text-right">Value now</th>
              <th className="text-right">Change</th>
              <th className="text-right">Held</th>
            </tr>
          </thead>
          <tbody>
            {data.positions.map((p) => {
              const change = p.valueCents - p.basisCents
              return (
                <tr key={p.id}>
                  <td>
                    <span className="font-semibold">{p.symbol}</span>
                    <span className="ml-2 text-xs" style={{ color: 'var(--color-faint)' }}>
                      {p.name}
                    </span>
                  </td>
                  <td className="tnum text-right" style={{ color: 'var(--color-muted)' }}>
                    {p.quantity.toPrecision(6)}
                  </td>
                  <td><StatusChip status={p.rarity} /></td>
                  <td className="tnum text-right">{cents(p.basisCents)}</td>
                  <td className="tnum text-right">{cents(p.valueCents)}</td>
                  <td
                    className="tnum text-right"
                    style={{ color: change >= 0 ? 'var(--color-up)' : 'var(--color-down)' }}
                  >
                    {cents(change, { sign: true })}
                  </td>
                  <td className="tnum text-right" style={{ color: 'var(--color-faint)' }}>
                    {timeAgo(p.acquiredAt)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </Panel>
  )
}

function Ledger({ data }: { data: AdminUserDetail }) {
  if (data.ledger.length === 0) return <Panel><Empty>No cash movements.</Empty></Panel>
  return (
    <Panel title="Cash ledger">
      <div className="overflow-x-auto">
        <table className="grid-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Kind</th>
              <th className="text-right">Amount</th>
              <th className="text-right">Balance after</th>
              <th>Memo</th>
            </tr>
          </thead>
          <tbody>
            {data.ledger.map((e) => (
              <tr key={e.id}>
                <td className="whitespace-nowrap" style={{ color: 'var(--color-muted)' }}>
                  {dateTime(e.createdAt)}
                </td>
                <td><StatusChip status={e.kind} /></td>
                <td
                  className="tnum text-right font-semibold"
                  style={{ color: e.amountCents >= 0 ? 'var(--color-up)' : 'var(--color-down)' }}
                >
                  {cents(e.amountCents, { sign: true })}
                </td>
                <td className="tnum text-right" style={{ color: 'var(--color-muted)' }}>
                  {cents(e.balanceAfterCents)}
                </td>
                <td className="text-xs" style={{ color: 'var(--color-faint)' }}>
                  {e.memo ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  )
}

function Settlement({ data }: { data: AdminUserDetail }) {
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Panel title="Deposits">
        {data.deposits.length === 0 ? (
          <Empty>None.</Empty>
        ) : (
          <table className="grid-table">
            <thead>
              <tr>
                <th>When</th>
                <th className="text-right">Amount</th>
                <th>Status</th>
                <th>Tx</th>
              </tr>
            </thead>
            <tbody>
              {data.deposits.map((d) => (
                <tr key={d.id}>
                  <td style={{ color: 'var(--color-muted)' }}>{dateTime(d.seenAt)}</td>
                  <td className="tnum text-right">{cents(d.amountCents)}</td>
                  <td><StatusChip status={d.status} /></td>
                  <td className="hash">{shortHash(d.txHash)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <Panel title="Withdrawals">
        {data.withdrawals.length === 0 ? (
          <Empty>None.</Empty>
        ) : (
          <table className="grid-table">
            <thead>
              <tr>
                <th>When</th>
                <th className="text-right">Amount</th>
                <th>Status</th>
                <th>Tx</th>
              </tr>
            </thead>
            <tbody>
              {data.withdrawals.map((w) => (
                <tr key={w.id}>
                  <td style={{ color: 'var(--color-muted)' }}>{dateTime(w.requestedAt)}</td>
                  <td className="tnum text-right">{cents(w.amountCents)}</td>
                  <td>
                    <StatusChip status={w.status} />
                    {w.failure && (
                      <div className="mt-1 text-[0.6875rem]" style={{ color: 'var(--color-down)' }}>
                        {w.failure}
                      </div>
                    )}
                  </td>
                  <td className="hash">{w.txHash ? shortHash(w.txHash) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  )
}

function Fairness({ data }: { data: AdminUserDetail }) {
  return (
    <Panel title="Seed pairs">
      <p className="border-b px-4 py-3 text-xs leading-relaxed" style={{ color: 'var(--color-muted)' }}>
        The server seed of an active pair is never shown — not here either. It is revealed only once
        the pair is retired, which is what makes the commitment worth anything.
      </p>
      {data.seeds.length === 0 ? (
        <Empty>No pairs.</Empty>
      ) : (
        <table className="grid-table">
          <thead>
            <tr>
              <th>Created</th>
              <th>Commitment</th>
              <th>Client seed</th>
              <th className="text-right">Rips</th>
              <th>State</th>
            </tr>
          </thead>
          <tbody>
            {data.seeds.map((s) => (
              <tr key={s.id}>
                <td style={{ color: 'var(--color-muted)' }}>{dateTime(s.createdAt)}</td>
                <td className="hash">{shortHash(s.serverSeedHash)}</td>
                <td className="hash">{s.clientSeed}</td>
                <td className="tnum text-right">{s.nonce}</td>
                <td>
                  <StatusChip status={s.active ? 'seen' : 'confirmed'} />
                  <span className="ml-2 text-xs" style={{ color: 'var(--color-faint)' }}>
                    {s.active ? 'live' : `retired ${s.retiredAt ? timeAgo(s.retiredAt) : ''}`}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  )
}
